import React, { useEffect, useRef, useState } from "react";
import climate_logo from "../assets/logo.png";

const PHONE = "+91 88559 41117";
const PHONE_TEL = "+918855941117";
const EMAIL = "climateacrepair@gmail.com";
const WHATSAPP = "https://wa.me/918855941117";

type ServiceItem = {
  icon: string;
  title: string;
  desc: string;
};

const services: ServiceItem[] = [
  {
    icon: "build",
    title: "AC Repair",
    desc: "Quick diagnostics and expert fixing of any fault.",
  },
  {
    icon: "home_repair_service",
    title: "Installation",
    desc: "Standardized mounting for optimal cooling.",
  },
  {
    icon: "propane",
    title: "Gas Refilling",
    desc: "Precision gas charging with high-quality refrigerant.",
  },
  {
    icon: "memory",
    title: "PCB Repair",
    desc: "Advanced circuit board troubleshooting.",
  },
  {
    icon: "calendar_today",
    title: "AMC Plans",
    desc: "Yearly maintenance for worry-free cooling.",
  },
  {
    icon: "cleaning_services",
    title: "Deep Cleaning",
    desc: "High-pressure jet wash for filters & coils.",
  },
  {
    icon: "install_desktop",
    title: "Uninstallation",
    desc: "Careful removal and safe transport packing.",
  },
  {
    icon: "leak_add",
    title: "Leakage Repair",
    desc: "Copper pipe repair and pressure testing.",
  },
  {
    icon: "apartment",
    title: "Cassette AC",
    desc: "Specialized care for commercial ceiling units.",
  },
  {
    icon: "engineering",
    title: "Full Overhaul",
    desc: "Complete system restoration for old units.",
  },
];

const brands = [
  "LG",
  "SAMSUNG",
  "DAIKIN",
  "VOLTAS",
  "BLUE STAR",
  "HITACHI",
  "CARRIER",
  "PANASONIC",
];

const areas = [
  "Baner",
  "Wakad",
  "Hinjewadi",
  "Kothrud",
  "Viman Nagar",
  "Hadapsar",
  "Aundh",
  "Kharadi",
  "Pimple Saudagar",
  "Pimpri-Chinchwad",
  "Shivajinagar",
  "Kondhwa",
  "Balewadi",
];

const reviews = [
  {
    name: "Rahul Deshmukh",
    area: "Baner",
    rating: 5,
    text: "Technician arrived within an hour on a Sunday. Gas refilling done at a very fair price. My split AC works like new. Highly recommended!",
  },
  {
    name: "Priya Kulkarni",
    area: "Kothrud",
    rating: 5,
    text: "Very professional installation of my new 1.5T split. Neat piping work and full explanation of maintenance. Best AC service in Pune.",
  },
  {
    name: "Amit Sharma",
    area: "Hinjewadi",
    rating: 5,
    text: "Signed up for the AMC plan for our office - 6 cassette units. Response time is genuinely under an hour. Saves us a lot of downtime.",
  },
];

const faqs = [
  {
    q: "How much does AC repair cost in Pune?",
    a: "Our visit and diagnostics charge starts at ₹399. The final cost depends on the fault. A gas refill for a 1.5T split typically ranges between ₹1800-₹2800. We share a transparent quote before starting any work.",
  },
  {
    q: "Do you offer same-day AC service in Pune?",
    a: "Yes. We provide fast doorstep service for major AC brands across Pune, including repair, installation, gas refilling and maintenance. Book before 2 PM for the best chance of same-day service.",
  },
  {
    q: "Which AC brands do you service?",
    a: "We service LG, Samsung, Daikin, Voltas, Blue Star, Hitachi, Carrier, Panasonic, Whirlpool, Godrej, Lloyd, O General and more.",
  },
  {
    q: "What are your working hours?",
    a: "We operate Monday to Sunday, 8:00 AM to 9:00 PM. For urgent requests outside these hours, WhatsApp us and we will try to arrange the earliest available slot.",
  },
  {
    q: "Do you provide a warranty on repairs?",
    a: "Yes. Repair work carries a service warranty and gas refilling comes with a leak-free assurance. Applicable terms are shared at billing.",
  },
];

const whyUs = [
  {
    icon: "schedule",
    title: "Same Day Visit",
    desc: "Booking before 2 PM? We aim to reach you the same day.",
  },
  {
    icon: "verified_user",
    title: "Verified Technicians",
    desc: "Certified technicians with the right tools for the job.",
  },
  {
    icon: "settings_backup_restore",
    title: "Genuine Spare Parts",
    desc: "Quality replacement parts selected for reliable performance.",
  },
];

const ClimateLanding: React.FC = () => {
  const glowRef = useRef<HTMLDivElement | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    const handler = (e: MouseEvent) => {
      glow.style.left = `${e.clientX}px`;
      glow.style.top = `${e.clientY}px`;
    };

    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.08 },
    );

    const sections = document.querySelectorAll<HTMLElement>("[data-reveal]");
    sections.forEach((section) => {
      section.classList.add(
        "opacity-0",
        "translate-y-8",
        "transition-all",
        "duration-1000",
        "ease-out",
      );
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const trackCall = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18299166788/ZvK-CL2v79EcEMTA3JVE",
      });
    }
  };

  return (
    <div
      id="top"
      className="min-h-screen overflow-x-hidden bg-[#f7f4eb] text-[#102f22]"
      style={{
        fontFamily:
          '"Inter", "DM Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Target-style fonts/icons. Keep these links in index.html if you already load them globally. */}
      <style>{`
        :root {
          --cream: #f7f4eb;
          --paper: #fbf9f2;
          --ink: #102f22;
          --muted: #6d736c;
          --line: rgba(16,47,34,.13);
          --lime: #b7f23a;
          --lime-dark: #86bf19;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          background: var(--cream);
        }

        .climate-serif {
          font-family: Georgia, "Times New Roman", serif;
          letter-spacing: -0.045em;
        }

        .climate-mono {
          font-family: "IBM Plex Mono", "SFMono-Regular", Consolas, monospace;
        }

        .climate-grid-bg {
          background-image:
            linear-gradient(rgba(16,47,34,.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(16,47,34,.025) 1px, transparent 1px);
          background-size: 42px 42px;
        }

        .climate-noise {
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: .12;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.22'/%3E%3C/svg%3E");
        }

        .climate-card {
          background: rgba(251,249,242,.68);
          border: 1px solid var(--line);
          box-shadow: 0 12px 35px rgba(16,47,34,.035);
        }

        .climate-card:hover {
          border-color: rgba(16,47,34,.25);
          box-shadow: 0 18px 45px rgba(16,47,34,.07);
        }

        .climate-highlight {
          position: relative;
          display: inline-block;
          z-index: 0;
        }

        .climate-highlight::after {
          content: "";
          position: absolute;
          z-index: -1;
          left: -3px;
          right: -5px;
          bottom: 8%;
          height: .34em;
          background: var(--lime);
          transform: rotate(-1.2deg);
        }

        .climate-float {
          animation: climateFloat 5s ease-in-out infinite;
        }

        @keyframes climateFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-9px); }
        }

        .climate-marquee {
          animation: climateMarquee 25s linear infinite;
        }

        @keyframes climateMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }

        .climate-service-card {
          min-height: 158px;
        }

        @media (max-width: 767px) {
          .climate-service-card {
            min-height: 145px;
          }
        }
      `}</style>

      <div
        ref={glowRef}
        className="pointer-events-none fixed z-[1] hidden h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#c8f76b]/20 blur-3xl md:block"
      />

      {/* NAVBAR */}
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-[#102f22]/10 bg-[#f7f4eb]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[66px] max-w-[980px] items-center justify-between px-5 md:px-0">
          <button
            onClick={() => scrollTo("top")}
            className="flex items-center gap-2.5"
            aria-label="Go to top"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#103b29] overflow-hidden">
              <img
                src={climate_logo}
                alt="Climate"
                className="h-6 w-6 object-contain"
              />
            </span>
            <span className="climate-serif text-[19px] font-bold tracking-[-0.04em]">
              Climate
            </span>
          </button>

          <div className="hidden items-center gap-7 md:flex">
            {[
              ["services", "Services"],
              ["why-us", "Why Us"],
              ["reviews", "Reviews"],
              ["faq", "FAQ"],
              ["contact", "Contact"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-[11px] font-medium text-[#697169] transition-colors hover:text-[#102f22]"
              >
                {label}
              </button>
            ))}
          </div>

          <a
            href={`tel:${PHONE_TEL}`}
            onClick={trackCall}
            className="flex items-center gap-2 rounded-full bg-[#103b29] px-4 py-2 text-[11px] font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#174b36]"
          >
            <span className="material-symbols-outlined text-[15px]">call</span>
            Call Now
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section
        data-reveal
        className="relative flex min-h-[720px] items-center overflow-hidden pt-[66px] climate-grid-bg"
      >
        <div className="climate-noise" />
        <div className="absolute -right-24 -top-24 h-[560px] w-[560px] rounded-full bg-[#d8f69a]/55 blur-[110px]" />
        <div className="absolute -bottom-40 -left-32 h-[430px] w-[430px] rounded-full bg-[#d9d4bf]/35 blur-[100px]" />

        <div className="relative z-10 mx-auto grid w-full max-w-[980px] items-center gap-10 px-5 py-16 md:grid-cols-[1.06fr_.94fr] md:px-0 md:py-20">
          <div>
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#102f22]/15 bg-[#fbf9f2]/70 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#8bc51f]" />
              <span className="climate-mono text-[9px] uppercase tracking-[.16em] text-[#697169]">
                Fast doorstep AC service across Pune
              </span>
            </div>

            <h1 className="climate-serif max-w-[690px] text-[52px] font-bold leading-[.93] text-[#103523] md:text-[74px] lg:text-[80px]">
              Affordable AC Repair,
              <br />
              Installation &{" "}
              <span className="climate-highlight italic font-normal">
                Gas Refilling
              </span>
              <br />
              in Pune.
            </h1>

            <p className="mt-8 max-w-[560px] text-[14px] leading-6 text-[#697169] md:text-[15px]">
              Professional AC repair, installation, gas refilling & AMC services
              with same-day doorstep support by certified engineers across Pune.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`tel:${PHONE_TEL}`}
                onClick={trackCall}
                className="inline-flex items-center gap-2 rounded-full bg-[#b7f23a] px-6 py-3 text-[12px] font-bold text-[#102f22] shadow-[0_8px_25px_rgba(120,160,30,.2)] transition-all hover:-translate-y-1 hover:bg-[#c4f65b]"
              >
                <span className="material-symbols-outlined text-[17px]">
                  call
                </span>
                Call Now
              </a>

              <button
                onClick={() => scrollTo("contact")}
                className="rounded-full border border-[#102f22]/20 bg-[#fbf9f2]/80 px-6 py-3 text-[12px] font-semibold text-[#102f22] transition-all hover:border-[#102f22]/40 hover:bg-white"
              >
                Book Service
              </button>
            </div>

            <div className="mt-12 grid max-w-[510px] grid-cols-3 border-t border-[#102f22]/15 pt-5">
              <div>
                <div className="climate-serif text-2xl font-bold">5000+</div>
                <div className="climate-mono mt-1 text-[8px] uppercase tracking-[.12em] text-[#777c76]">
                  Happy Customers
                </div>
              </div>
              <div className="border-l border-[#102f22]/10 pl-5">
                <div className="climate-serif text-2xl font-bold">10+</div>
                <div className="climate-mono mt-1 text-[8px] uppercase tracking-[.12em] text-[#777c76]">
                  Years Exp.
                </div>
              </div>
              <div className="border-l border-[#102f22]/10 pl-5">
                <div className="climate-serif text-2xl font-bold">60m</div>
                <div className="climate-mono mt-1 text-[8px] uppercase tracking-[.12em] text-[#777c76]">
                  Response Time
                </div>
              </div>
            </div>
          </div>

          <div className="relative flex min-h-[430px] items-end justify-center md:min-h-[510px]">
            <div className="absolute right-0 top-10 h-[380px] w-[380px] rounded-full bg-[#d4f57c]/45 blur-[85px]" />
            <div className="absolute bottom-2 right-0 h-[380px] w-[310px] rounded-[36px] bg-[#10101b] shadow-2xl md:h-[430px] md:w-[365px]" />
            <div className="absolute bottom-0 right-[-5px] h-[375px] w-[350px] rounded-[28px] border-2 border-[#b7f23a]/90 md:h-[425px] md:w-[390px]" />

            <img
              alt="Modern AC unit"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuCL10igql11uUlkGncLZTZF6KHIC-7OcvsCE-MNPcgq11YDTv1tIq3S2Gm4aHM2dA8AVYVzvFsm33OiKzAdPcdTL2luKYITphMsI_pBj9oYUMKt-9WBU6dlzDhRy5peB65aZMjPdye-RVO3I6tc5_5fCPPf89UVIrcxAM5Ea25PzJTJXUusel61kxQfO73HZMmTlIy89JPFIi3PnzvUnLKEhm3u1c1cO-fwMUO5uTkkUbg7ECaqDit2Qvj9Cjk_puLoKyKMMV-CwbZr"
              className="climate-float relative z-10 w-[88%] max-w-[430px] rounded-[28px] object-cover shadow-2xl md:w-full"
            />

            <div className="absolute bottom-6 left-0 z-20 rounded-2xl border border-white/30 bg-[#fbf9f2]/90 p-3 shadow-xl backdrop-blur-md">
              <div className="climate-mono text-[8px] uppercase tracking-[.14em] text-[#697169]">
                Certified engineers
              </div>
              <div className="mt-1 text-xs font-bold text-[#102f22]">
                Repair • Install • Maintain
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="services"
        data-reveal
        className="border-t border-[#102f22]/10 bg-[#f7f4eb] py-20 md:py-24"
      >
        <div className="mx-auto max-w-[980px] px-5 md:px-0">
          <div className="mb-12 grid gap-6 md:grid-cols-[1.3fr_.7fr] md:items-end">
            <div>
              <div className="mb-4 flex items-center gap-3">
                <span className="climate-mono text-[10px] text-[#7d837b]">
                  01
                </span>
                <span className="h-px w-10 bg-[#102f22]/20" />
                <span className="climate-mono text-[9px] uppercase tracking-[.2em] text-[#7d837b]">
                  What we do
                </span>
              </div>
              <h2 className="climate-serif text-[48px] font-bold leading-[.95] md:text-[60px]">
                Premium AC Services
                <br />
                <span className="italic font-normal">in Pune</span>
              </h2>
            </div>

            <p className="max-w-[300px] text-[13px] leading-6 text-[#697169] md:justify-self-end">
              Expert services for all residential and commercial AC units with
              specialized tools and genuine parts.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-5 md:gap-3">
            {services.map((service) => (
              <div
                key={service.title}
                className="climate-card climate-service-card group rounded-2xl p-5 text-center transition-all duration-500 hover:-translate-y-1"
              >
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#f0eee4] text-[#244a38] transition-all duration-300 group-hover:bg-[#b7f23a]">
                  <span className="material-symbols-outlined text-[19px]">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-[13px] font-bold text-[#173728]">
                  {service.title}
                </h3>
                <p className="mt-2 text-[10px] leading-[1.55] text-[#777b75]">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section
        id="why-us"
        data-reveal
        className="border-t border-[#102f22]/10 bg-[#fbf9f2] py-20 md:py-24"
      >
        <div className="mx-auto grid max-w-[980px] items-center gap-12 px-5 md:grid-cols-[1fr_.94fr] md:px-0">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="climate-mono text-[10px] text-[#7d837b]">
                02
              </span>
              <span className="h-px w-10 bg-[#102f22]/20" />
              <span className="climate-mono text-[9px] uppercase tracking-[.2em] text-[#7d837b]">
                Why us
              </span>
            </div>

            <h2 className="climate-serif text-[48px] font-bold leading-[.95] md:text-[60px]">
              Why Choose <span className="italic font-normal">Climate</span>
              <br />
              Pune?
            </h2>

            <p className="mt-6 max-w-[520px] text-[13px] leading-6 text-[#697169]">
              We bridge the gap between technical precision and doorstep
              convenience. Every technician is background verified and carries a
              full toolkit.
            </p>

            <div className="relative mt-10 space-y-7 pl-1 before:absolute before:bottom-4 before:left-[21px] before:top-4 before:w-px before:bg-[#102f22]/12">
              {whyUs.map((item) => (
                <div key={item.title} className="relative z-10 flex gap-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#102f22]/12 bg-[#fbf9f2]">
                    <span className="material-symbols-outlined text-[18px] text-[#234b38]">
                      {item.icon}
                    </span>
                  </div>
                  <div className="pt-1">
                    <h3 className="text-[13px] font-bold">{item.title}</h3>
                    <p className="mt-1 max-w-[390px] text-[11px] leading-5 text-[#777b75]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 rounded-[30px] border border-[#102f22]/10" />
            <div className="overflow-hidden rounded-[25px] border border-[#102f22]/10 bg-[#ece8dc] p-2">
              <img
                alt="Professional AC technician"
                className="aspect-[4/3] w-full rounded-[20px] object-cover grayscale transition-all duration-700 hover:grayscale-0"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuBMF01F2fLrft2iovK4egbhBdVI9lp5m4TBm0qVywnU79koA95aGdOlihE8bdHw4OgjA15XuVr-a_QzawuDDtye3-1ESIii909UVPXYeDkyBVelFQXMeCIxupgH-6JhXlzyl3fP7oHwfFVgYYuDGZHf__-QsR4nXdl9MIYNzuKEnCHpeS_ZSyMIXVf7XDQyLKHZXiey-iiRz4nNFU7TxTAZqjR9NHoTYKnO0T58UPwe_Vxhgj-hePNM7WzNF5qUKOowHg1WwyrmRm4f"
              />
            </div>
            <div className="absolute bottom-5 left-5 rounded-full bg-[#103b29] px-4 py-2 text-[9px] font-semibold uppercase tracking-[.12em] text-white">
              Certified engineers only
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="overflow-hidden border-y border-[#102f22]/10 bg-[#f7f4eb] py-8">
        <div className="climate-marquee flex w-max items-center">
          {[...brands, ...brands, ...brands].map((brand, index) => (
            <span
              key={`${brand}-${index}`}
              className="climate-serif px-8 text-[22px] font-bold text-[#b7b8ad] md:px-12 md:text-[28px]"
            >
              {brand}
            </span>
          ))}
        </div>
      </section>

      {/* AREAS */}
      <section
        data-reveal
        className="border-b border-[#102f22]/10 bg-[#fbf9f2] py-20"
      >
        <div className="mx-auto grid max-w-[980px] gap-12 px-5 md:grid-cols-[.85fr_1.15fr] md:px-0">
          <div>
            <div className="climate-mono mb-3 text-[9px] uppercase tracking-[.2em] text-[#7d837b]">
              Service coverage
            </div>
            <h2 className="climate-serif text-[46px] font-bold leading-[.95]">
              Serving all of <span className="italic font-normal">Pune</span>
            </h2>
            <p className="mt-5 max-w-[390px] text-[12px] leading-6 text-[#697169]">
              Our technicians are strategically placed across the city to
              provide fast doorstep AC service.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-y-3">
              {areas.map((area) => (
                <div
                  key={area}
                  className="flex items-center gap-2 text-[11px] text-[#4f5b52]"
                >
                  <span className="material-symbols-outlined text-[14px] text-[#7ead24]">
                    location_on
                  </span>
                  {area}
                </div>
              ))}
            </div>
          </div>

          <div className="relative min-h-[360px] overflow-hidden rounded-[28px] border border-[#102f22]/10 bg-[#e8e4d7]">
            <img
              alt="Pune service coverage"
              className="absolute inset-0 h-full w-full object-cover opacity-55 grayscale contrast-110"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuC5l3Ftyl7HGBqwqimWk9m1HgTS7daAGXp4P7nv9DzjljYlgn8rslSv9ctdE1aim-1ndavU5-s9JggoYBGbIYQQ9QcmcDl5oED0YPbMJ6vp-6e8VywsOgd-5w4vx5yphtsnfnUk884-mbMdDkrmsIYYCor6Rwa-dyI38xxr4S7QU630fD9nl8vUWnM836dzyl3t5OitByeQYwB2oIoj6Brl31VhvbOwi2uOG4onPVPkLQ83BYmdvfS-75sYlbJSA5Iqy7ZyZQTqxguz"
            />
            <div className="absolute inset-0 bg-[#f7f4eb]/25" />
            {[
              "left-[28%] top-[27%]",
              "left-[56%] top-[48%]",
              "right-[24%] bottom-[23%]",
            ].map((position, index) => (
              <span
                key={index}
                className={`absolute ${position} flex h-4 w-4 animate-ping rounded-full bg-[#9bd52d]`}
                style={{ animationDelay: `${index * 700}ms` }}
              />
            ))}
            <div className="absolute bottom-5 left-5 rounded-full border border-[#102f22]/10 bg-[#fbf9f2]/90 px-4 py-2 text-[9px] font-semibold uppercase tracking-[.15em] text-[#355141] backdrop-blur">
              Pune doorstep coverage
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section
        id="reviews"
        data-reveal
        className="border-b border-[#102f22]/10 bg-[#f7f4eb] py-20"
      >
        <div className="mx-auto max-w-[980px] px-5 md:px-0">
          <div className="mb-12 text-center">
            <div className="climate-mono mb-3 text-[9px] uppercase tracking-[.2em] text-[#7d837b]">
              Customer reviews
            </div>
            <h2 className="climate-serif text-[46px] font-bold leading-[.95] md:text-[56px]">
              What our{" "}
              <span className="italic font-normal">Pune customers</span> say
            </h2>
            <p className="mx-auto mt-4 max-w-[540px] text-[12px] leading-6 text-[#697169]">
              Real reviews from homeowners and business owners across Pune.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {reviews.map((review) => (
              <article
                key={review.name}
                className="climate-card rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="mb-4 flex gap-0.5 text-[#9b9e8e]">
                  {Array.from({ length: review.rating }).map((_, index) => (
                    <span
                      key={index}
                      className="material-symbols-outlined text-[15px]"
                    >
                      star
                    </span>
                  ))}
                </div>
                <p className="text-[12px] leading-6 text-[#626a63]">
                  “{review.text}”
                </p>
                <div className="mt-5 border-t border-[#102f22]/10 pt-4">
                  <div className="text-[12px] font-bold">{review.name}</div>
                  <div className="climate-mono mt-1 text-[8px] uppercase tracking-[.14em] text-[#7ead24]">
                    {review.area}, Pune
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-reveal
        className="border-b border-[#102f22]/10 bg-[#fbf9f2] py-20"
      >
        <div className="mx-auto max-w-[780px] px-5 md:px-0">
          <div className="mb-10 text-center">
            <div className="climate-mono mb-3 text-[9px] uppercase tracking-[.2em] text-[#7d837b]">
              FAQ
            </div>
            <h2 className="climate-serif text-[46px] font-bold leading-[.95]">
              Frequently Asked{" "}
              <span className="italic font-normal">Questions</span>
            </h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={faq.q}
                  className="overflow-hidden rounded-2xl border border-[#102f22]/10 bg-[#f7f4eb]/70"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    className="flex w-full items-center justify-between gap-5 px-5 py-5 text-left"
                  >
                    <span className="text-[13px] font-semibold text-[#173728]">
                      {faq.q}
                    </span>
                    <span
                      className={`material-symbols-outlined shrink-0 text-[19px] text-[#55725f] transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      expand_more
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-[12px] leading-6 text-[#697169]">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-reveal className="bg-[#f7f4eb] py-20 md:py-24">
        <div className="mx-auto max-w-[980px] px-5 md:px-0">
          <div className="relative overflow-hidden rounded-[30px] bg-[#103b29] px-7 py-10 text-white md:px-12 md:py-12">
            <div className="absolute -right-24 -top-28 h-72 w-72 rounded-full bg-[#b7f23a]/20 blur-3xl" />
            <div className="relative grid gap-10 md:grid-cols-[1fr_.8fr] md:items-center">
              <div>
                <div className="climate-mono text-[9px] uppercase tracking-[.2em] text-[#b7f23a]">
                  Need AC service?
                </div>
                <h2 className="climate-serif mt-4 text-[46px] font-bold leading-[.95] md:text-[58px]">
                  Get your AC
                  <br />
                  <span className="italic font-normal">fixed today.</span>
                </h2>
                <p className="mt-5 max-w-[470px] text-[12px] leading-6 text-white/65">
                  Call us directly for fast doorstep service across Pune, or
                  message us on WhatsApp for an appointment.
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={`tel:${PHONE_TEL}`}
                    onClick={trackCall}
                    className="inline-flex items-center gap-2 rounded-full bg-[#b7f23a] px-5 py-3 text-[11px] font-bold text-[#102f22] transition-all hover:-translate-y-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      call
                    </span>
                    {PHONE}
                  </a>
                  <a
                    href={WHATSAPP}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-[11px] font-semibold text-white transition-all hover:bg-white/10"
                  >
                    WhatsApp Us
                  </a>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href={`mailto:${EMAIL}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors hover:bg-white/10"
                >
                  <div className="climate-mono text-[8px] uppercase tracking-[.15em] text-white/45">
                    Email Inquiry
                  </div>
                  <div className="mt-2 break-all text-[13px] font-semibold">
                    {EMAIL}
                  </div>
                </a>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <div className="climate-mono text-[8px] uppercase tracking-[.15em] text-white/45">
                    Working Hours
                  </div>
                  <div className="mt-2 text-[13px] font-semibold">
                    Mon-Sun · 8:00 AM - 9:00 PM
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#102f22]/10 bg-[#0c241a] text-white">
        <div className="mx-auto grid max-w-[980px] gap-10 px-5 py-12 md:grid-cols-[1.2fr_1fr_1fr] md:px-0">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#b7f23a]">
                <img
                  src={climate_logo}
                  alt="Climate"
                  className="h-6 w-6 object-contain"
                />
              </span>
              <span className="climate-serif text-[20px] font-bold">
                Climate
              </span>
            </div>
            <p className="mt-5 max-w-[300px] text-[11px] leading-6 text-white/45">
              Precision climate engineering for a cooler Pune. Trusted for AC
              repair, installation, gas refilling and maintenance.
            </p>
          </div>

          <div>
            <h3 className="climate-mono text-[9px] uppercase tracking-[.18em] text-white/45">
              Services
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-2 text-[11px] text-white/60">
              {[
                "AC Repair",
                "AC Installation",
                "Gas Refilling",
                "Deep Cleaning",
                "PCB Repair",
                "AMC Plans",
              ].map((service) => (
                <button
                  key={service}
                  onClick={() => scrollTo("services")}
                  className="text-left transition-colors hover:text-[#b7f23a]"
                >
                  {service}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="climate-mono text-[9px] uppercase tracking-[.18em] text-white/45">
              Contact
            </h3>
            <div className="mt-4 space-y-3 text-[11px] text-white/60">
              <a
                href={`tel:${PHONE_TEL}`}
                onClick={trackCall}
                className="block transition-colors hover:text-[#b7f23a]"
              >
                {PHONE}
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="block break-all transition-colors hover:text-[#b7f23a]"
              >
                {EMAIL}
              </a>
              <button
                onClick={() => scrollTo("contact")}
                className="transition-colors hover:text-[#b7f23a]"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto max-w-[980px] px-5 py-5 text-center text-[9px] uppercase tracking-[.12em] text-white/30 md:px-0">
            © {new Date().getFullYear()} Climate Pune. Precision Climate
            Engineering.
          </div>
        </div>
      </footer>

      {/* FLOATING CALL BUTTON */}
      <a
        href={`tel:${PHONE_TEL}`}
        onClick={trackCall}
        aria-label="Call Climate AC Repair"
        className="group fixed bottom-5 left-1/2 z-[100] flex w-[126px] -translate-x-1/2 items-center justify-center gap-2 rounded-full border-2 border-[#103b29] bg-[#b7f23a] px-3 py-2.5 text-[#102f22] shadow-[0_12px_40px_rgba(120,160,30,.28)] transition-all duration-300 hover:-translate-y-1 hover:scale-105"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#103b29] text-white">
          <span className="material-symbols-outlined text-[17px]">call</span>
        </span>
        <span className="flex flex-col text-left leading-tight">
          <span className="text-[8px] font-medium text-[#405348]">
            Need help?
          </span>
          <span className="text-[11px] font-bold">Call Now</span>
        </span>
      </a>
    </div>
  );
};

export default ClimateLanding;
